// src/components/Login.js
import React, { useState } from "react";
import {
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useTheme } from "../ThemeContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { theme } = useTheme();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    const auth = getAuth();
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError("Failed to sign in. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const trimmedEmail = email.trim();

    setError("");
    setSuccess("");

    if (!trimmedEmail) {
      setError("Enter your email address to receive a password reset link.");
      return;
    }

    setResetLoading(true);
    const auth = getAuth();

    try {
      await sendPasswordResetEmail(auth, trimmedEmail, {
        url: `${window.location.origin}/login`,
      });
      setSuccess("If an account exists for that email, a password reset link has been sent.");
    } catch (error) {
      if (error.code === "auth/invalid-email") {
        setError("Enter a valid email address to receive a password reset link.");
      } else if (error.code === "auth/user-not-found") {
        setSuccess("If an account exists for that email, a password reset link has been sent.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many reset attempts. Please wait a moment and try again.");
      } else {
        setError("We could not send a password reset email. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh", // Changed from minHeight to height
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.loginBackground, // Replaced hardcoded gradient with theme property
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 4,
            background: "#ffffff",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
          }}
        >
          <Box
            component="img"
            src="/logo4.png"
            alt="Fort Corps Logo"
            sx={{
              width: "auto",
              height: 80,
              mb: 2,
            }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: "800",
              color: "#333",
              mb: 1,
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "-0.5px",
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 4, textAlign: "center" }}
          >
            Sign in to continue to Fort Corps
          </Typography>

          {error && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                width: "100%",
                bgcolor: "#ffebee",
                color: "#c62828",
                borderRadius: 2,
                fontSize: "0.875rem",
                textAlign: "center",
                border: "1px solid #ef9a9a",
              }}
            >
              {error}
            </Box>
          )}

          {success && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                width: "100%",
                bgcolor: "#e8f5e9",
                color: "#2e7d32",
                borderRadius: 2,
                fontSize: "0.875rem",
                textAlign: "center",
                border: "1px solid #a5d6a7",
              }}
            >
              {success}
            </Box>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: "100%" }}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                type="button"
                variant="text"
                onClick={handlePasswordReset}
                disabled={loading || resetLoading}
                sx={{
                  minHeight: 32,
                  px: 0,
                  color: "#334155",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "transparent",
                    color: "#0f172a",
                    textDecoration: "underline",
                  },
                }}
              >
                {resetLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Forgot password?"
                )}
              </Button>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: "1rem",
                fontWeight: "bold",
                textTransform: "none",
                background: "linear-gradient(45deg, #334155 30%, #0f172a 90%)",
                boxShadow: "0 3px 5px 2px rgba(15, 23, 42, .3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #475569 30%, #1e293b 90%)",
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
            </Button>
          </Box>
        </Paper>
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 4, color: "rgba(255,255,255,0.8)" }}
        >
          © {new Date().getFullYear()} Fort Corps. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Login;
